import {
  LastOrderProperty,
  TimeUnit,
  type LastOrderCondition as LastOrderConditionType,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import {
  DateQueryOperator,
  NumericQueryOperator,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo } from "react";

const propertyOptions = [
  {
    id: LastOrderProperty.TIME,
    type: "text" as const,
    text: "Order time",
    value: LastOrderProperty.TIME,
  },
  {
    id: LastOrderProperty.VALUE,
    type: "text" as const,
    text: "Order value",
    value: LastOrderProperty.VALUE,
  },
  {
    id: LastOrderProperty.ITEM_COUNT,
    type: "text" as const,
    text: "Number of items",
    value: LastOrderProperty.ITEM_COUNT,
  },
];

export const LastOrderCondition = memo(function LastOrderCondition({
  condition,
  onChange,
}: {
  condition: LastOrderConditionType;
  onChange: (updated: LastOrderConditionType) => void;
}) {
  const handlePropertyChange = useCallback(
    (property: LastOrderProperty) => {
      switch (property) {
        case LastOrderProperty.TIME:
          onChange({
            conditionType: condition.conditionType,
            property: LastOrderProperty.TIME,
            queryOperator: DateQueryOperator.WITHIN_LAST,
            unit: TimeUnit.DAYS,
          });
          break;
        case LastOrderProperty.VALUE:
          onChange({
            conditionType: condition.conditionType,
            property: LastOrderProperty.VALUE,
            queryOperator: NumericQueryOperator.GREATER_THAN,
            value: 0,
          });
          break;
        case LastOrderProperty.ITEM_COUNT:
          onChange({
            conditionType: condition.conditionType,
            property: LastOrderProperty.ITEM_COUNT,
            queryOperator: NumericQueryOperator.GREATER_THAN,
            value: 0,
          });
          break;
      }
    },
    [condition.conditionType, onChange],
  );

  const selectedProperty = useMemo(
    () => propertyOptions.find((opt) => opt.value === condition.property),
    [condition.property],
  );

  return (
    <Flex dir="column" gap="md" w="full">
      <RedoSingleSelectDropdownInput
        options={propertyOptions}
        optionSelected={({ item }) => handlePropertyChange(item.value)}
        placeholder="Select property"
        selectedItem={selectedProperty}
      />
      {condition.property === LastOrderProperty.TIME && (
        <TimeInputs condition={condition} onChange={onChange} />
      )}
      {(condition.property === LastOrderProperty.VALUE ||
        condition.property === LastOrderProperty.ITEM_COUNT) && (
        <NumericInputs
          condition={condition}
          onChange={onChange}
          unit={condition.property === LastOrderProperty.VALUE ? "$" : "items"}
        />
      )}
    </Flex>
  );
});

const timeUnitOptions = [
  {
    id: TimeUnit.MINUTES,
    type: "text" as const,
    text: "minutes",
    value: TimeUnit.MINUTES,
  },
  {
    id: TimeUnit.HOURS,
    type: "text" as const,
    text: "hours",
    value: TimeUnit.HOURS,
  },
  {
    id: TimeUnit.DAYS,
    type: "text" as const,
    text: "days",
    value: TimeUnit.DAYS,
  },
];

const TimeInputs = memo(function TimeInputs({
  condition,
  onChange,
}: {
  condition: Extract<LastOrderConditionType, { property: "time" }>;
  onChange: (updated: LastOrderConditionType) => void;
}) {
  const parseDate = (
    dateStr: string | undefined,
  ): Temporal.PlainDate | undefined => {
    if (!dateStr) {
      return undefined;
    }
    try {
      return Temporal.PlainDate.from(dateStr);
    } catch {
      return undefined;
    }
  };

  switch (condition.queryOperator) {
    case DateQueryOperator.WITHIN_LAST:
      return (
        <Flex align="center" gap="sm">
          <RedoTextInput
            inputTypeHint="number"
            min={1}
            placeholder="0"
            setValue={(val: string) => {
              const num = parseInt(val, 10);
              onChange({ ...condition, value: isNaN(num) ? undefined : num });
            }}
            size="sm"
            value={condition.value?.toString() ?? ""}
          />
          <RedoSingleSelectDropdownInput
            options={timeUnitOptions}
            optionSelected={({ item }) =>
              onChange({ ...condition, unit: item.value })
            }
            selectedItem={timeUnitOptions.find(
              (opt) => opt.value === (condition.unit ?? TimeUnit.DAYS),
            )}
          />
        </Flex>
      );

    case DateQueryOperator.ON_OR_BEFORE:
    case DateQueryOperator.ON_OR_AFTER:
      return (
        <RedoSingleDatePicker
          buttonSize="sm"
          date={parseDate(condition.date)}
          setDate={(date: Temporal.PlainDate) =>
            onChange({ ...condition, date: date.toString() })
          }
        />
      );

    case DateQueryOperator.BETWEEN:
      return (
        <Flex align="center" gap="sm">
          <RedoSingleDatePicker
            buttonSize="sm"
            date={parseDate(condition.startDate)}
            setDate={(date: Temporal.PlainDate) =>
              onChange({ ...condition, startDate: date.toString() })
            }
          />
          <Text color="secondary" fontSize="sm">
            and
          </Text>
          <RedoSingleDatePicker
            buttonSize="sm"
            date={parseDate(condition.endDate)}
            setDate={(date: Temporal.PlainDate) =>
              onChange({ ...condition, endDate: date.toString() })
            }
          />
        </Flex>
      );

    case DateQueryOperator.NEVER:
      return null;

    default:
      return null;
  }
});

const NumericInputs = memo(function NumericInputs({
  condition,
  onChange,
  unit,
}: {
  condition: Extract<
    LastOrderConditionType,
    { property: "value" | "item_count" }
  >;
  onChange: (updated: LastOrderConditionType) => void;
  unit: string;
}) {
  switch (condition.queryOperator) {
    case NumericQueryOperator.GREATER_THAN:
    case NumericQueryOperator.LESS_THAN:
    case NumericQueryOperator.EQUAL_TO:
      return (
        <Flex align="center" gap="sm">
          {unit === "$" && (
            <Text color="secondary" fontSize="sm">
              $
            </Text>
          )}
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(value: string) => {
              const num = parseFloat(value);
              onChange({ ...condition, value: isNaN(num) ? 0 : num });
            }}
            size="sm"
            value={condition.value.toString()}
          />
          {unit !== "$" && (
            <Text color="secondary" fontSize="sm">
              {unit}
            </Text>
          )}
        </Flex>
      );

    case NumericQueryOperator.BETWEEN:
      return (
        <Flex align="center" gap="sm">
          {unit === "$" && (
            <Text color="secondary" fontSize="sm">
              $
            </Text>
          )}
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(value: string) => {
              const num = parseFloat(value);
              onChange({ ...condition, minValue: isNaN(num) ? 0 : num });
            }}
            size="sm"
            value={condition.minValue.toString()}
          />
          <Text color="secondary" fontSize="sm">
            and
          </Text>
          {unit === "$" && (
            <Text color="secondary" fontSize="sm">
              $
            </Text>
          )}
          <RedoTextInput
            inputTypeHint="number"
            min={0}
            placeholder="0"
            setValue={(value: string) => {
              const num = parseFloat(value);
              onChange({ ...condition, maxValue: isNaN(num) ? 0 : num });
            }}
            size="sm"
            value={condition.maxValue.toString()}
          />
          {unit !== "$" && (
            <Text color="secondary" fontSize="sm">
              {unit}
            </Text>
          )}
        </Flex>
      );

    default:
      return null;
  }
});
